/**
 * Atlas PM · 认证 API
 *
 * 对应后端 AuthController（apps/api/src/modules/auth/auth.controller.ts）：
 *   POST /api/auth/login   → { data: { accessToken, expiresIn, user } }
 *   POST /api/auth/logout  → { data: { success } }
 *   GET  /api/auth/me      → { data: CurrentUser }
 *   POST /api/auth/refresh → { data: { accessToken, expiresIn, user } }
 *
 * ⚠️ 实际后端**不返回 refreshToken**（仅 accessToken + expiresIn）。
 *    本文件类型以真实返回为准（已用 curl 验证）。
 */

import { api } from './client';

/** 数据范围（ABAC），scopeType 为数字枚举：0 ALL / 1 REGION / 2 PROVINCE / 3 CITY / 4 SELF */
export interface DataScope {
  scopeType: number;
  orgIds: string[];
  provinceCodes: string[];
  /** 显式项目白名单：scopeType=4（仅参与项目）时唯一可见来源 */
  projectIds: string[];
}

/** 当前登录用户（对应 /auth/me 返回，与后端 ICurrentUser 对齐） */
export interface CurrentUser {
  userId: string;
  username: string;
  displayName: string;
  orgId: string;
  isSuperAdmin: boolean;
  roles: string[];
  permissions: string[];
  dataScope: DataScope;
}

/** 登录 / 刷新令牌的返回体 */
export interface LoginResult {
  accessToken: string;
  /** 有效期（秒） */
  expiresIn: number;
  user: CurrentUser;
}

export const authApi = {
  /** 用户名口令登录 */
  login: (username: string, password: string) =>
    api.post<LoginResult>('/auth/login', { username, password }),

  /** 登出（后端会清权限缓存；失败不阻塞前端登出） */
  logout: () => api.post<{ success: boolean }>('/auth/logout'),

  /** 获取当前用户（含扁平化权限列表） */
  me: () => api.get<CurrentUser>('/auth/me'),

  /** 刷新访问令牌 */
  refresh: () => api.post<LoginResult>('/auth/refresh'),
};
